// One build serves two hostnames; App.jsx picks the route tree from this.
//   carfido.com / staging.carfido.com                 -> ConsumerApp
//   partner.carfido.com / partner-staging.carfido.com -> PartnerApp
// API calls stay relative, so nginx maps each host to the right backend port.

// The real hosts are SINGULAR — partner.carfido.com, partner-staging.carfido.com,
// partner.localhost — and every URL this module builds uses that spelling. The
// optional "s" here is deliberate slack on the matching side only: if anyone ever
// lands on a plural alias or an old bookmark, they still get the partner portal
// instead of the driver app. Never copy the plural into a generated URL. The
// trailing (-|\.) stops it matching unrelated names like partnership.example.com.
const PARTNER_HOST_PATTERN = /^partners?(-|\.)/i;
const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1']);

function isLocalHostname(hostname = '') {
    return LOCAL_HOSTNAMES.has(String(hostname || '').toLowerCase());
}

function defaultPartnerSiteOrigin() {
    if (typeof window === 'undefined') return 'https://partner.carfido.com';
    const { protocol, hostname, port } = window.location;
    if (isLocalHostname(hostname)) {
        return `${protocol}//partner.localhost${port ? `:${port}` : ''}`;
    }
    const normalizedHostname = String(hostname || '').toLowerCase().replace(/^www\./, '');
    if (normalizedHostname === 'staging.carfido.com') {
        return `${protocol}//partner-staging.carfido.com`;
    }
    return 'https://partner.carfido.com';
}

// Baked in at build time by CRA, so it must live in dash_website/.env.
export const PARTNER_SITE_ORIGIN = (
    process.env.REACT_APP_PARTNER_SITE_URL || defaultPartnerSiteOrigin()
).replace(/\/$/, '');

// The hostname cannot change without a full page load, so resolve it once.
const IS_PARTNER_SITE = (() => {
    if (String(process.env.REACT_APP_SITE || '').trim().toLowerCase() === 'partner') return true;
    if (typeof window === 'undefined') return false;
    return PARTNER_HOST_PATTERN.test(window.location.hostname);
})();

export function isPartnerSite() {
    return IS_PARTNER_SITE;
}

export function isLocalDevHost() {
    if (typeof window === 'undefined') return false;
    return isLocalHostname(window.location.hostname);
}

export function partnerLoginUrl() {
    return IS_PARTNER_SITE ? '/login' : `${PARTNER_SITE_ORIGIN}/login`;
}

export function partnerSignupUrl() {
    return IS_PARTNER_SITE ? '/signup' : `${PARTNER_SITE_ORIGIN}/signup`;
}

export function partnerForgotPasswordUrl() {
    return IS_PARTNER_SITE ? '/forgot-password' : `${PARTNER_SITE_ORIGIN}/forgot-password`;
}

export function partnerDashboardUrl() {
    return IS_PARTNER_SITE ? '/' : `${PARTNER_SITE_ORIGIN}/`;
}

// The driver site for whichever environment we are in, derived by dropping the
// "partner." / "partner-" prefix from the current host:
//   partner.carfido.com          -> carfido.com
//   partner-staging.carfido.com  -> staging.carfido.com
//   partner.localhost:2650       -> localhost:2650
// Deriving it keeps staging and production correct with no extra env var to
// forget; REACT_APP_DRIVER_SITE_URL overrides it if a host ever breaks the
// naming convention.
export function driverSiteUrl() {
    const configured = String(process.env.REACT_APP_DRIVER_SITE_URL || '').trim();
    if (configured) return configured.replace(/\/$/, '');
    if (typeof window === 'undefined') return 'https://carfido.com';

    const { protocol, host } = window.location;
    if (!IS_PARTNER_SITE) return `${protocol}//${host}`;
    return `${protocol}//${host.replace(/^partners?[-.]/i, '')}`;
}
